'use client'

import { motion } from 'motion/react'
import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { usePrefersReducedMotion } from '../morphDialog/usePrefersReducedMotion'

type AnimatedStepProps = {
  stepKey: string
  // 1 when moving forward (new step slides in from the right), -1 going back.
  direction?: 1 | -1
}

// vaul's drawer curve, so a height change reads as the sheet itself resizing.
const HEIGHT_EASE = [0.32, 0.72, 0, 1] as const
const CONTENT_EASE = [0.23, 1, 0.32, 1] as const

// Swaps a multi-step sheet's content without the sheet snapping to its new
// height: the height follows the content and the incoming step slides in.
export function AnimatedStep({
  stepKey,
  direction = 1,
  children,
}: PropsWithChildren<AnimatedStepProps>) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number | 'auto'>('auto')
  const [hasMounted, setHasMounted] = useState(false)
  const prefersReducedMotion = usePrefersReducedMotion()

  useEffect(() => {
    setHasMounted(true)
  }, [])

  useEffect(() => {
    const element = contentRef.current

    if (!element || typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(([entry]) => {
      setHeight(entry.borderBoxSize[0].blockSize)
    })

    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  const shouldAnimate = hasMounted && !prefersReducedMotion

  return (
    <motion.div
      className="overflow-hidden"
      initial={false}
      animate={{ height }}
      transition={
        shouldAnimate ? { duration: 0.35, ease: HEIGHT_EASE } : { duration: 0 }
      }
    >
      <div ref={contentRef}>
        <motion.div
          key={stepKey}
          initial={shouldAnimate ? { opacity: 0, x: 16 * direction } : false}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.24, ease: CONTENT_EASE }}
        >
          {children}
        </motion.div>
      </div>
    </motion.div>
  )
}
